import { z } from 'zod';

/**
 * Instant booking confirmation ("auto-approve").
 *
 * Each business chooses how online bookings get confirmed:
 *   - 'approval' — every booking is a request the owner approves (default).
 *   - 'instant'  — confirmed straight away when the slot is still free.
 *
 * The authoritative decision runs server-side in
 * `submit_customer_booking_request` (migration 20261102100000). This module is
 * its TypeScript mirror — the same rule, unit-tested — plus the settings
 * schema and the customer-facing copy for each outcome. Keep the two in step.
 */

export const BOOKING_CONFIRMATION_MODES = ['approval', 'instant'] as const;
export type BookingConfirmationMode = (typeof BOOKING_CONFIRMATION_MODES)[number];

export interface BookingConfirmationSettings {
  mode: BookingConfirmationMode;
  /** Instant mode only: put a free cleaner on the job. */
  autoAssign: boolean;
}

export const bookingConfirmationSettingsSchema = z
  .object({
    mode: z.enum(BOOKING_CONFIRMATION_MODES),
    autoAssign: z.boolean(),
  })
  // Auto-assign only means anything when bookings confirm instantly.
  .transform((value): BookingConfirmationSettings => ({
    mode: value.mode,
    autoAssign: value.mode === 'instant' && value.autoAssign,
  }));

export const DEFAULT_BOOKING_CONFIRMATION: BookingConfirmationSettings = {
  mode: 'approval',
  autoAssign: false,
};

/** Read the company_info columns, defaulting anything missing to "I'll approve". */
export function normaliseBookingConfirmation(raw: {
  booking_confirmation_mode?: string | null;
  booking_auto_assign?: boolean | null;
} | null | undefined): BookingConfirmationSettings {
  const mode: BookingConfirmationMode = raw?.booking_confirmation_mode === 'instant' ? 'instant' : 'approval';
  return { mode, autoAssign: mode === 'instant' && raw?.booking_auto_assign === true };
}

/** Plain-English explanation of the auto-assign rule, shown in settings. */
export const AUTO_ASSIGN_RULE =
  'We’ll put a cleaner on the job who’s free for the whole slot. If more than one is free, it goes to whoever has the fewest jobs that day (then that week). If nobody is free, the booking is still confirmed and waits in Unassigned for you.';

/** Shown under the multi-cleaner caveat in settings. */
export const MULTI_CLEANER_RULE =
  'Jobs that need two or more cleaners are only confirmed instantly when auto-assign can staff them fully. Otherwise they come to you as a request.';

// ---------------------------------------------------------------------------
// Decision rule (mirror of the SQL)
// ---------------------------------------------------------------------------

export interface FreeCleaner {
  id: string;
  /** Non-cancelled jobs this cleaner has that day. */
  jobsThatDay: number;
  /** Non-cancelled jobs this cleaner has that week (Mon–Sun). */
  jobsThatWeek: number;
  /** When they joined the team (ISO); longest-serving wins a tie. */
  joinedAt: string;
}

/**
 * Least-booked first: fewest jobs that day, then that week, then longest on
 * the team. Takes as many as the job needs.
 */
export function pickAutoAssignees(free: FreeCleaner[], required: number): string[] {
  return [...free]
    .sort(
      (a, b) =>
        a.jobsThatDay - b.jobsThatDay ||
        a.jobsThatWeek - b.jobsThatWeek ||
        a.joinedAt.localeCompare(b.joinedAt) ||
        a.id.localeCompare(b.id),
    )
    .slice(0, Math.max(1, required))
    .map((cleaner) => cleaner.id);
}

export type InstantBlocker =
  | 'approval_mode'
  | 'service_not_bookable'
  | 'business_paused'
  | 'invalid_slot'
  | 'slot_too_short'
  | 'slot_in_past'
  | 'outside_opening_hours'
  | 'no_availability'
  | 'needs_more_cleaners';

export interface BookingDecisionInput {
  settings: BookingConfirmationSettings;
  /** Cleaners the service needs (services.resource_required, at least 1). */
  required: number;
  serviceBookable: boolean;
  businessPaused: boolean;
  startMinutes: number;
  endMinutes: number;
  /** Service length in minutes when there are no variants; null to skip the check. */
  serviceMinutes: number | null;
  /** The slot starts after "now" on the business's clock. */
  inFuture: boolean;
  /** The slot sits inside that day's opening hours. */
  withinOpeningHours: boolean;
  /** Team members whose availability covers the whole slot. */
  capacity: number;
  /** Non-cancelled jobs overlapping the slot. */
  booked: number;
  /** Cleaners free for the whole slot (only consulted with auto-assign on). */
  freeCleaners: FreeCleaner[];
}

export type BookingDecision =
  | { outcome: 'rejected'; reason: 'slot_taken' }
  | { outcome: 'request'; reason: InstantBlocker }
  | { outcome: 'instant'; assigneeIds: string[] };

/**
 * What happens to an online booking at submit time.
 *   - A slot with no room left is rejected in both modes (customer picks another).
 *   - 'approval' mode → always a request.
 *   - 'instant' mode → confirmed only when every check passes; otherwise it
 *     quietly falls back to a request (never an error for the customer).
 */
export function decideBookingConfirmation(input: BookingDecisionInput): BookingDecision {
  const required = Math.max(1, input.required);

  if (input.capacity > 0 && input.capacity - input.booked < required) {
    return { outcome: 'rejected', reason: 'slot_taken' };
  }
  if (input.settings.mode !== 'instant') return { outcome: 'request', reason: 'approval_mode' };
  if (!input.serviceBookable) return { outcome: 'request', reason: 'service_not_bookable' };
  if (input.businessPaused) return { outcome: 'request', reason: 'business_paused' };
  if (input.endMinutes <= input.startMinutes) return { outcome: 'request', reason: 'invalid_slot' };
  if (input.serviceMinutes != null && input.endMinutes - input.startMinutes < input.serviceMinutes) {
    return { outcome: 'request', reason: 'slot_too_short' };
  }
  if (!input.inFuture) return { outcome: 'request', reason: 'slot_in_past' };
  if (!input.withinOpeningHours) return { outcome: 'request', reason: 'outside_opening_hours' };
  if (input.capacity === 0) return { outcome: 'request', reason: 'no_availability' };

  const assigneeIds = input.settings.autoAssign ? pickAutoAssignees(input.freeCleaners, required) : [];
  if (required > 1 && assigneeIds.length < required) {
    return { outcome: 'request', reason: 'needs_more_cleaners' };
  }
  return { outcome: 'instant', assigneeIds };
}

// ---------------------------------------------------------------------------
// Customer-facing result
// ---------------------------------------------------------------------------

export type BookingOutcome = 'instant' | 'request';

/** Interpret the submit RPC's result. Anything unexpected reads as a request. */
export function readBookingOutcome(result: unknown): BookingOutcome {
  const value = result as { confirmation?: unknown; status?: unknown } | null | undefined;
  return value?.confirmation === 'instant' || value?.status === 'confirmed' ? 'instant' : 'request';
}

export interface BookingSuccessCopy {
  toastTitle: string;
  heading: string;
  body: string;
}

export function bookingSuccessCopy(
  outcome: BookingOutcome,
  { isRepeat = false, when }: { isRepeat?: boolean; when?: string } = {},
): BookingSuccessCopy {
  if (outcome === 'instant') {
    return {
      toastTitle: 'You’re booked in!',
      heading: 'You’re booked in',
      body: `${when ? `We’ll see you ${when}. ` : ''}${
        isRepeat ? 'Your regular cleans are booked in too. ' : ''
      }We’ve emailed your confirmation and a private link to view your bookings and messages.`,
    };
  }
  return {
    toastTitle: 'Booking request sent!',
    heading: 'Request sent — we’ll confirm shortly',
    body: `${
      isRepeat
        ? 'We’ll check your first clean and confirm it shortly, then book in your regular visits. '
        : 'We’ll check the time works and confirm it shortly. '
    }We’ve emailed you a private link to view your bookings and messages.`,
  };
}

/** One line under the booking form, so customers know what to expect. */
export function bookingModeNote(mode: BookingConfirmationMode): string {
  return mode === 'instant'
    ? 'Free slots are confirmed straight away. You’ll get an email with the details.'
    : 'This sends a booking request. We’ll check it and confirm by email shortly.';
}
